/**
 * Canonical contact type registry.
 *
 * Single source of truth for all contact types in the app. Every file that
 * needs type metadata (icons, labels, input modes, categories) should import
 * from here.
 *
 * Categories:
 *   phone-like — uses PhoneInputField (dial code + numeric)
 *   at-prefix  — text input with @ prefix
 *   text       — plain text input
 *
 * NOTE: Signal and Telegram both support usernames in addition to phone numbers
 * (power user feature). Currently defaulting to phone-like input. Future work:
 * allow user to choose phone or username entry for these types.
 */

import { Phone } from '@phosphor-icons/react/dist/ssr/Phone';
import { EnvelopeSimpleOpen } from '@phosphor-icons/react/dist/ssr/EnvelopeSimpleOpen';
import { InstagramLogo } from '@phosphor-icons/react/dist/ssr/InstagramLogo';
import { LinkedinLogo } from '@phosphor-icons/react/dist/ssr/LinkedinLogo';
import { WhatsappLogo } from '@phosphor-icons/react/dist/ssr/WhatsappLogo';
import { FacebookLogo } from '@phosphor-icons/react/dist/ssr/FacebookLogo';
import { GithubLogo } from '@phosphor-icons/react/dist/ssr/GithubLogo';
import { SnapchatLogo } from '@phosphor-icons/react/dist/ssr/SnapchatLogo';
import { TelegramLogo } from '@phosphor-icons/react/dist/ssr/TelegramLogo';
import { TiktokLogo } from '@phosphor-icons/react/dist/ssr/TiktokLogo';
import { TwitchLogo } from '@phosphor-icons/react/dist/ssr/TwitchLogo';
import { YoutubeLogo } from '@phosphor-icons/react/dist/ssr/YoutubeLogo';
import { LinkSimple } from '@phosphor-icons/react/dist/ssr/LinkSimple';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { IconSignal } from '../components/UISystem/Icons/IconSignal';
import { IconSubstack } from '../components/UISystem/Icons/IconSubstack';
import { IconXLogo } from '../components/UISystem/Icons/IconXLogo';

// -----------------------------------------------------------------------------
// Registry
// -----------------------------------------------------------------------------

export const ALL_CONTACT_TYPES = {
  phone: {
    id: 'phone',
    label: 'Phone',
    Icon: Phone,
    inputMode: 'tel',
    placeholder: 'Number',
    category: 'phone-like',
    getLink: (value) => `tel:${value}`,
  },
  whatsapp: {
    id: 'whatsapp',
    label: 'WhatsApp',
    Icon: WhatsappLogo,
    inputMode: 'tel',
    placeholder: 'Number',
    category: 'phone-like',
    getLink: (value) => `https://wa.me/${value.replace(/\D/g, '')}`,
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    Icon: InstagramLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'at-prefix',
    hasAtPrefix: true,
    getLink: (value) => `https://instagram.com/${value}`,
  },
  telegram: {
    id: 'telegram',
    label: 'Telegram',
    Icon: TelegramLogo,
    inputMode: 'tel',
    placeholder: 'Username',
    category: 'phone-like',
    getLink: (value) => `https://t.me/${value}`,
  },
  signal: {
    id: 'signal',
    label: 'Signal',
    Icon: IconSignal,
    inputMode: 'tel',
    placeholder: 'Number',
    category: 'phone-like',
    getLink: (value) => `https://signal.me/#p/${value}`,
  },
  email: {
    id: 'email',
    label: 'Email',
    Icon: EnvelopeSimpleOpen,
    inputMode: 'email',
    placeholder: 'Email',
    category: 'text',
    getLink: (value) => `mailto:${value}`,
  },
  website: {
    id: 'website',
    label: 'Website',
    Icon: LinkSimple,
    inputMode: 'url',
    placeholder: 'Website URL',
    category: 'text',
    getLink: (value) => {
      if (!value) return null;
      if (value.startsWith('http://') || value.startsWith('https://')) return value;
      return `https://${value.replace(/^\/+/, '')}`;
    },
  },
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    Icon: LinkedinLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://linkedin.com/in/${value}`,
  },
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    Icon: FacebookLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://facebook.com/${value}`,
  },
  github: {
    id: 'github',
    label: 'GitHub',
    Icon: GithubLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://github.com/${value}`,
  },
  snapchat: {
    id: 'snapchat',
    label: 'Snapchat',
    Icon: SnapchatLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://snapchat.com/add/${value}`,
  },
  substack: {
    id: 'substack',
    label: 'Substack',
    Icon: IconSubstack,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://${value}.substack.com`,
  },
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    Icon: TiktokLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'at-prefix',
    hasAtPrefix: true,
    getLink: (value) => `https://tiktok.com/@${value}`,
  },
  twitch: {
    id: 'twitch',
    label: 'Twitch',
    Icon: TwitchLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://twitch.tv/${value}`,
  },
  twitter: {
    id: 'twitter',
    label: 'X/Twitter',
    Icon: IconXLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'at-prefix',
    hasAtPrefix: true,
    getLink: (value) => `https://x.com/${value}`,
  },
  youtube: {
    id: 'youtube',
    label: 'YouTube',
    Icon: YoutubeLogo,
    inputMode: 'text',
    placeholder: 'Username',
    category: 'text',
    getLink: (value) => `https://youtube.com/@${value}`,
  },
  custom: {
    id: 'custom',
    label: 'Custom',
    Icon: LinkSimple,
    inputMode: 'url',
    placeholder: 'Website',
    category: 'text',
    copyOnly: true,
    getLink: (value) => {
      if (!value) return null;
      if (value.startsWith('http://') || value.startsWith('https://')) return value;
      if (value.startsWith('www.')) return `https://${value}`;
      return null;
    },
  },
  chat: {
    id: 'chat',
    label: 'Chat',
    Icon: ChatCircle,
    inputMode: 'text',
    placeholder: '',
    category: 'text',
  },
};

// Import + re-export from contactKeys.js (icon-free, safe for non-UI imports)
import {
  CONTACT_FIELDS,
  ALL_CONTACT_KEYS,
  BASELINE_TYPES,
  RELEVANCE_ORDER,
  getBaseType,
  openContactLink,
} from './contactKeys';
export {
  CONTACT_FIELDS,
  ALL_CONTACT_KEYS,
  BASELINE_TYPES,
  RELEVANCE_ORDER,
  getBaseType,
  openContactLink,
};

// -----------------------------------------------------------------------------
// Functions
// -----------------------------------------------------------------------------

// Build dropdown options: Profile's shared + baseline, no duplicates.
// `baseline` — the always-offered types (default BASELINE_TYPES, main's old
// guest overlay); the new design's guest sheets pass Phone · Instagram · Email
// (CD Drop 17, SIGNUP_Anon Sheet 2 rule 3: WhatsApp out).
export const getAvailableContactTypes = (profileData, { baseline = BASELINE_TYPES } = {}) => {
  const profileSharedFields = profileData?.sharedFields || [];

  // Start with Profile's shared contact fields (in order they appear)
  const fromProfile = CONTACT_FIELDS.filter((f) => profileSharedFields.includes(f));

  // Add baseline if not already included
  const combined = [...new Set([...fromProfile, ...baseline])];

  // Convert to type objects
  return combined.map((id) => ALL_CONTACT_TYPES[id]).filter(Boolean);
};

// Get default contact type based on Profile's shared fields (reciprocity)
//
// Rules:
// - Single contact type shared → that's the default, regardless of what it is
// - Multiple shared → pick the highest on the relevance hierarchy
// - None / only non-contact fields → phone
// - custom is never a default
export const getDefaultContactType = (profileData) => {
  const sharedFields = profileData?.sharedFields || [];

  // Extract contact types only, strip "2" suffix variants, deduplicate
  const contactTypes = [
    ...new Set(sharedFields.map(getBaseType).filter((f) => CONTACT_FIELDS.includes(f))),
  ];

  if (contactTypes.length === 0) return 'phone';
  if (contactTypes.length === 1) return contactTypes[0];

  // Multiple — pick highest on relevance hierarchy
  for (const type of RELEVANCE_ORDER) {
    if (contactTypes.includes(type)) return type;
  }

  // None in hierarchy — default to phone
  return 'phone';
};
